/**
 * PlaygroundChartRenderer
 *
 * Main component for rendering Playground charts using ECharts.
 * Routes to appropriate ECharts adapter based on PlaygroundType and
 * renders using the shared EChartRenderer component.
 */

import React, { useMemo } from "react";
import { PlaygroundType } from "@/services/api/widget/types";
import { EChartRenderer } from "@/components/common/report-builder/EChartRenderer";
import { useCompanyColors } from "@/hooks/useCompanyColors";
import { getChartSeriesColors } from "@/components/chart/styles/colors";
import { useIsDarkMode } from "@/utils/colorScheme";
import type { ChartSettings } from "@/components/common/report-builder/constants";

// Import adapters
import {
  buildPerformanceHistoryOption,
  buildExposureOption,
  buildExposureColumnsOption,
  buildScatterChartOption,
  buildRollingStatsOption,
  buildLookthroughOption,
  buildExposureOverTimeOption,
} from "../adapters/echarts";

import type {
  PlaygroundChartSettings,
  VisualSettings,
  DataPoint,
  ExposureBandsMap,
} from "../adapters/echarts/types";

export interface PlaygroundChartRendererProps {
  /** CSS class name for the container */
  className?: string;
  /** The playground type determines which adapter to use */
  playgroundType: PlaygroundType;
  /** Raw data from PlaygroundContext */
  rawData: DataPoint[] | null;
  /** Asset statistics for scatter charts and exposure-over-time metadata */
  assetStats?: Record<string, unknown>;
  /** Array of selected asset IDs */
  selectedAssets?: string[];
  /** Map of asset IDs to display names */
  assetDisplayNames?: Record<string, string>;
  /** Map of asset IDs to metadata */
  assetMetadata?: Record<string, Record<string, unknown>>;
  /** Map of asset IDs to colors */
  assetColors?: Record<string, string>;
  /** Chart settings from playground */
  chartSettings: Partial<PlaygroundChartSettings>;
  /** Saved min/max/target bands for exposure columns chart */
  exposureBands?: ExposureBandsMap | null;
  /** Whether data is loading */
  isLoading: boolean;
  // Visual settings
  smoothLines?: boolean;
  showAreaGradient?: boolean;
  showGridColumns?: boolean;
  showGridRows?: boolean;
  showCrosshair?: boolean;
  showPatternBackground?: boolean;
  showTooltips?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
}

export const PlaygroundChartRenderer: React.FC<PlaygroundChartRendererProps> = ({
  className = "",
  playgroundType,
  rawData,
  assetStats,
  selectedAssets = [],
  assetDisplayNames = {},
  assetMetadata = {},
  assetColors = {},
  chartSettings,
  exposureBands = null,
  isLoading,
  // Visual settings with defaults
  smoothLines = false,
  showAreaGradient = true,
  showGridColumns = false,
  showGridRows = true,
  showCrosshair = true,
  showPatternBackground = false,
  showTooltips = true,
  showXAxis = true,
  showYAxis = true,
}) => {
  // Get company colors for chart palette
  const { chartColors: companyChartColors } = useCompanyColors();
  const colorPalette = useMemo(
    () => getChartSeriesColors(companyChartColors),
    [companyChartColors]
  );

  // Track current theme via document class + storage (matches app shell)
  const isDarkMode = useIsDarkMode();

  // Combine visual settings
  const visualSettings: VisualSettings = useMemo(
    () => ({
      smoothLines,
      showAreaGradient,
      showGridColumns,
      showGridRows,
      showCrosshair,
      showPatternBackground,
      showTooltips,
      showXAxis,
      showYAxis,
    }),
    [
      smoothLines,
      showAreaGradient,
      showGridColumns,
      showGridRows,
      showCrosshair,
      showPatternBackground,
      showTooltips,
      showXAxis,
      showYAxis,
    ]
  );

  // Build base adapter props
  const baseProps = useMemo(
    () => ({
      rawData: rawData || [],
      selectedAssets,
      assetDisplayNames,
      assetMetadata,
      assetColors,
      isDarkMode,
      colorPalette,
      visualSettings,
    }),
    [
      rawData,
      selectedAssets,
      assetDisplayNames,
      assetMetadata,
      assetColors,
      isDarkMode,
      colorPalette,
      visualSettings,
    ]
  );

  // Build ECharts option based on playground type
  const { option, hasData } = useMemo(() => {
    // Return empty option if loading or no data
    if (isLoading || !rawData || rawData.length === 0) {
      return { option: null, hasData: false };
    }

    switch (playgroundType) {
      case PlaygroundType.PerformanceHistory:
        return buildPerformanceHistoryOption({
          ...baseProps,
          assetStats,
          chartSettings: {
            displayMode: chartSettings.displayMode || "performance",
            rebaseValue: chartSettings.rebaseValue || 100,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      case PlaygroundType.Exposure:
        return buildExposureOption({
          ...baseProps,
          chartSettings: {
            exposureType: chartSettings.exposureType || "asset_allocation",
            combineVariants: chartSettings.combineVariants || false,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      case PlaygroundType.ExposureColumns:
        return buildExposureColumnsOption({
          ...baseProps,
          exposureBands,
          chartSettings: {
            exposureType: chartSettings.exposureType || "asset_allocation",
            combineVariants: chartSettings.combineVariants || false,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      case PlaygroundType.ExposureOverTime:
        return buildExposureOverTimeOption({
          ...baseProps,
          chartSettings: {
            exposureDisplayMode: chartSettings.exposureDisplayMode || "scaled_weights",
            categories: (assetStats?.metadata as { categories?: string[] } | undefined)
              ?.categories,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition || "right",
          },
        });

      case PlaygroundType.ScatterChart:
        if (!assetStats) {
          return { option: null, hasData: false };
        }
        return buildScatterChartOption({
          ...baseProps,
          assetStats: assetStats as Record<string, { x: number; y: number; name?: string }>,
          chartSettings: {
            xStatistic: chartSettings.xStatistic || "returns",
            yStatistic: chartSettings.yStatistic || "volatility",
            xLabel: chartSettings.xLabel || "Return",
            yLabel: chartSettings.yLabel || "Volatility",
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      case PlaygroundType.MetricsOverTime:
        return buildRollingStatsOption({
          ...baseProps,
          chartSettings: {
            statistic: "volatility",
            windowSize: 12,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      case PlaygroundType.RollingStats:
        return buildRollingStatsOption({
          ...baseProps,
          assetStats,
          chartSettings: {
            statistic: chartSettings.statistic || "volatility",
            windowSize: chartSettings.windowSize || 12,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      case PlaygroundType.Underwater:
        return buildRollingStatsOption({
          ...baseProps,
          assetStats,
          chartSettings: {
            statistic: "underwater",
            windowSize: chartSettings.windowSize || 12,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      case PlaygroundType.Lookthrough:
        return buildLookthroughOption({
          ...baseProps,
          chartSettings: {
            showPercentage: true,
            precision: 1,
            legendMode: chartSettings.legendMode,
            legendPosition: chartSettings.legendPosition,
          },
        });

      default:
        console.warn(`Unsupported playground type: ${playgroundType}`);
        return { option: null, hasData: false };
    }
  }, [playgroundType, baseProps, chartSettings, assetStats, rawData, isLoading, exposureBands]);

  // Map playground legend settings to EChartRenderer settings
  const echartsSettings: Partial<ChartSettings> = useMemo(() => {
    const settings: Partial<ChartSettings> = {
      legend: {
        show: true,
      },
      legendIcon: "circle",
      showGrid: showGridRows || showGridColumns,
      gridColor: isDarkMode ? "#475569" : "#e2e8f0",
    };

    const legendPosition = chartSettings.legendPosition || "right";
    settings.legendPosition = legendPosition;

    // Map legend position
    if (legendPosition === "right") {
      settings.legendLayout = "grid";
      settings.legendColumns = 1;
    } else {
      settings.legendLayout = "horizontal";
    }

    return settings;
  }, [chartSettings.legendPosition, showGridRows, showGridColumns, isDarkMode]);

  // Don't render if no option
  if (!option || !hasData) {
    return (
      <div className={`relative w-full h-full flex justify-center items-center ${className}`}>
        <div className="text-gray-400 dark:text-gray-500">
          {isLoading ? "Loading chart..." : "No data available"}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative w-full h-full flex justify-center items-center ${className}`}
      data-testid="playground-chart-renderer"
    >
      <EChartRenderer
        option={option}
        theme={isDarkMode ? "dark" : "light"}
        settings={echartsSettings}
        style={{ width: "100%", height: "100%" }}
        renderer="canvas"
      />
    </div>
  );
};

export default PlaygroundChartRenderer;
